import { z } from 'zod';

/**
 * The wildcard action. A key ending in `.manage` grants every action inside its
 * namespace (`verification.manage` ⊃ `verification.review`), and the bare
 * `manage` key is the global wildcard held only by `super_admin`.
 */
export const MANAGE_ACTION = 'manage';

/** The global wildcard key — grants everything (N8). */
export const GLOBAL_MANAGE: PermissionKey = MANAGE_ACTION;

/**
 * A permission key: `<resource>.<action>`, the global `manage`, or a namespaced
 * form with more than two segments — `integrations.<capability>.manage` (N21)
 * is derived from the integrations catalog rather than written out by hand, so
 * the grammar has to allow the extra level.
 *
 * Segments are lower-kebab so a key is stable across a database, a JWT claim,
 * a URL, and an admin checkbox id.
 */
const PERMISSION_KEY_PATTERN =
  /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*(?:\.[a-z][a-z0-9]*(?:-[a-z0-9]+)*)*$/;

export const PermissionKeySchema = z
  .string()
  .min(1)
  .regex(
    PERMISSION_KEY_PATTERN,
    'A permission key is dot-separated lower-kebab segments, e.g. "verification.review" or "manage".',
  );

export type PermissionKey = z.infer<typeof PermissionKeySchema>;

/** The namespace a key belongs to — `''` for the global `manage`. */
export function permissionResource(key: PermissionKey): string {
  const lastDot = key.lastIndexOf('.');
  return lastDot === -1 ? '' : key.slice(0, lastDot);
}

/** The action a key authorizes — the last segment. */
export function permissionAction(key: PermissionKey): string {
  const lastDot = key.lastIndexOf('.');
  return lastDot === -1 ? key : key.slice(lastDot + 1);
}

/** Whether the key is a wildcard: the global `manage`, or any `<namespace>.manage`. */
export function isWildcardKey(key: PermissionKey): boolean {
  return permissionAction(key) === MANAGE_ACTION;
}
